/**
 * The bag's behavior half (studio-shop D4): the bag kept in this browser,
 * priced by the server each time it shows. Lines keep the bag's order; a line
 * the server no longer sells stays, marked, until the buyer removes it, as does
 * one with fewer left than its quantity (shop-self-fulfilment D5), and
 * checkout carries only the available ones. Going to checkout keeps today's
 * prices as the ones the buyer saw. On the bag page it always shows; placed on
 * a custom page it is the page's drawer, open while the page's shop says so,
 * and leaving it closes it first. Draws nothing.
 */
import { useMemo } from 'react';
import { shownOptionLabel } from '@telify/shared/shop/line-label';

import { shippingLine, shopMoney, shopPrice, useBagPrice, useShopCatalog } from '../../api/shop';
import { usePieceOptions, usePlaced, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { BAG_MAX_QUANTITY, useBag } from '../../hooks/useBag';
import { usePlaceOnShopPage, useShopPage } from '../../hooks/useShopPage';
import { usePageMover } from '../../pageMover';
import { contract, type ShopBagActions, type ShopBagEvents, type ShopBagLine, type ShopBagState } from './contract';

export type ShopBagRuntime = PieceRuntime<typeof contract, ShopBagState, ShopBagActions> & {
  /** Where it shows: the bag page, or a drawer over a custom page. */
  readonly form: 'page' | 'drawer';
};

export function useShopBag(input: PieceInput<ShopBagEvents> = {}): ShopBagRuntime {
  const options = usePieceOptions(contract, input.options);
  const drawer = usePlaced();
  const { page, state: shopPage } = useShopPage();
  usePlaceOnShopPage(page, 'drawers', drawer);
  const move = usePageMover();
  const bag = useBag();
  const priced = useBagPrice(bag.lines);
  const catalog = useShopCatalog().data;
  const answer = priced.data;

  const lines = useMemo<ShopBagLine[]>(() => {
    if (!answer) return [];
    const byOption = new Map(answer.lines.map((line) => [line.optionId, line]));
    return bag.lines.flatMap((kept) => {
      const line = byOption.get(kept.optionId);
      if (!line) return [];
      // Look check item 5: a line's prices as items are priced ($168); the subtotal is money math ($168.00).
      const money = (amount: string | null) => (amount === null ? null : shopPrice(amount, answer.currency));
      return [
        {
          optionId: kept.optionId,
          name: line.productTitle,
          // shop-self-fulfilment D2: a label that only repeats the name is left out.
          option: line.optionLabel === null ? null : shownOptionLabel(line.productTitle ?? '', line.optionLabel),
          imageUrl: line.imageUrl,
          size: line.size,
          canvas: line.canvas,
          quantity: kept.quantity,
          unitPrice: money(line.unitPrice),
          total: money(line.lineTotal),
          was: line.available && line.priceChanged && kept.price ? money(kept.price) : null,
          available: line.available,
          soldOut: line.soldOut,
          left: line.left,
          claimed: Boolean(kept.answers),
        },
      ];
    });
  }, [answer, bag.lines]);

  const { setQuantity, remove, rememberPrices } = bag;
  const actions = useMemo<ShopBagActions>(() => {
    const close = () => page.closeBag();
    return {
      setQuantity,
      remove,
      checkout: () => {
        if (answer) rememberPrices(answer.lines);
        if (drawer) close();
        move('/shop/checkout');
      },
      keepShopping: () => (drawer ? close() : move('/')),
      open: () => page.openBag(),
      close,
    };
  }, [setQuantity, remove, rememberPrices, answer, drawer, page, move]);

  const status: ShopBagState['status'] =
    bag.lines.length === 0 ? 'empty' : priced.isError && !answer ? 'failed' : !answer ? 'loading' : 'ready';
  return {
    state: {
      open: !drawer || shopPage.bagOpen,
      status,
      lines,
      subtotal: answer ? shopMoney(answer.subtotal, answer.currency) : null,
      count: bag.count,
      canCheckout: lines.some((line) => line.available),
      problem: priced.isError ? priced.error.message : null,
      maxQuantity: BAG_MAX_QUANTITY,
      shipping: shippingLine(catalog?.freeShippingCountries ?? []),
    },
    actions,
    options,
    form: drawer ? 'drawer' : 'page',
  };
}
